import { TFunction } from 'i18next';
import { Role } from '../types/auth';
import { OrderState, OrderType } from '../types/orders';
import { getVisibleStatesForRole } from '../constants/roleStateVisibility';
import { getValidStatesForEntityType } from '../constants/entityStateFiltering';

export const generateRoidPadding = (
  count: number,
  countLimit: number
): string => {
  let newCount = count.toString();
  const countSize = newCount.length;
  if (countSize < countLimit) {
    newCount = newCount.padStart(countLimit, '0');
  }
  return newCount;
};

// Function to generate state options with translations
export const getStateOptions = (t: TFunction) => [
  { label: t('common:states.open'), value: 'open' },
  { label: t('common:states.managed'), value: 'managed' },
  { label: t('common:states.pending-work'), value: 'pending-work' },
  {
    label: t('common:states.work-in-progress'),
    value: 'work-in-progress',
  },
  {
    label: t('common:states.pending-validation'),
    value: 'pending-validation',
  },
  {
    label: t('common:states.pending-at-validation'),
    value: 'pending-at-validation',
  }, // T66
  { label: t('common:states.validated'), value: 'validated' },
  {
    label: t('common:states.pending-certification'),
    value: 'pending-certification',
  }, // Actions: post-validación, pendiente de cobro a Generalitat
  { label: t('common:states.partial'), value: 'partial' },
  { label: t('common:states.annulled'), value: 'annulled' },
  { label: t('common:states.end'), value: 'end' },
  // D5 — estados propios del Recorregut de Vigilància. Están aquí para que
  // esta lista sea el enum COMPLETO: quien recorta es
  // `getStateOptionsByRoleAndEntityType` con ENTITY_VALID_STATES, y ninguna
  // otra entidad los admite. Antes faltaban y había que inyectarlos aparte.
  { label: t('common:states.in-progress'), value: 'in-progress' },
  { label: t('common:states.paused'), value: 'paused' },
];

/**
 * Get state options filtered by user role
 * Only returns states that the role can view according to ROLE_VISIBLE_STATES
 *
 * @param t - Translation function
 * @param role - User role to filter states
 * @returns Filtered array of state options
 *
 * @example
 * ```typescript
 * const options = getStateOptionsByRole(t, Role.Tablet);
 * // Returns only: open, pending-work, work-in-progress, pending-validation, annulled
 * ```
 */
export const getStateOptionsByRole = (t: TFunction, role: Role) => {
  const allOptions = getStateOptions(t);
  const visibleStates = getVisibleStatesForRole(role);

  return allOptions.filter((option) =>
    visibleStates.includes(option.value as any)
  );
};

/**
 * Opciones del filtro Estado de una tabla.
 *
 * Se aplican DOS recortes, y el segundo es condicional a propósito:
 *
 * 1. **Por entidad** (siempre): `ENTITY_VALID_STATES[tipo]` — los estados que
 *    esa entidad puede tener de verdad. Una Actuación no está nunca
 *    «pendiente de trabajo»; ofrecerlo devolvería 0 filas siempre.
 *
 * 2. **Por rol** (`listingFiltersByRole`): `ROLE_VISIBLE_STATES[rol]`, y SOLO
 *    si el backend que alimenta esa tabla recorta también sus filas por rol.
 *    Aplicarlo donde el backend no recorta esconde el mando de filas que el
 *    usuario está viendo: el caso real es un Tablet mirando una tabla de
 *    actuaciones con filas «Anulada» y un desplegable que solo le ofrecía
 *    «Abierta», porque `ROLE_VISIBLE_STATES[Tablet]` describe la máquina de
 *    estados de la OT PADRE y la de una Actuación no comparte con ella más
 *    que `open`.
 *
 * Quién recorta y quién no vive en `ROLE_FILTERED_LISTING_TABLE_IDS`
 * (`components/display/tables/types.ts`), junto al helper del mismo nombre que
 * las tablas usan para rellenar este parámetro.
 *
 * `entityType` admite un `OrderType`, un array (vistas multi-entidad como
 * Multimedia, que agrega Action + Accident y filtra por la UNIÓN) o
 * `undefined` (no recorta por entidad).
 *
 * @see docs/superpowers/specs/2026-05-21-multimedia-state-filter-union-design.md
 * @see BUG #29 — Validated y Partial son exclusivos de Action
 */
export const getStateOptionsByRoleAndEntityType = (
  t: TFunction,
  role: Role,
  entityType: OrderType | OrderType[] | undefined,
  /**
   * ¿El backend de esta tabla recorta las filas por rol? Obligatorio a
   * propósito: un valor por defecto es justo lo que dejaría a la próxima tabla
   * heredando en silencio el recorte que causó el bug.
   */
  listingFiltersByRole: boolean
): ReturnType<typeof getStateOptions> => {
  const base = listingFiltersByRole
    ? getStateOptionsByRole(t, role)
    : getStateOptions(t);

  if (!entityType) return base;

  const types = Array.isArray(entityType) ? entityType : [entityType];
  if (types.length === 0) return base;

  const validStates = new Set<OrderState>(
    types.flatMap((type) => getValidStatesForEntityType(type))
  );
  return base.filter((opt) => validStates.has(opt.value as OrderState));
};

// Fase 4 nav Trabajo/Revisión (2026-07-12) — valores válidos de "contenedor
// D0" (Origen) para el filtro/columna Origen de las 4 tablas GLOBALES de
// entidad (Actuaciones/Accidentes/Comunicaciones/Multimedia). Subconjunto de
// OrderType: solo los 3 tipos que pueden actuar como contenedor D0 (mismo
// conjunto que valida el backend en `containerTypes` — ver
// hierarchy-validator.ts en rois-back).
export const CONTAINER_TYPE_VALUES: string[] = [
  OrderType.Generic,
  OrderType.Campaign,
  OrderType.SurveillanceRoute,
];

/**
 * ¿Es esta actuación una **Actuació bàsica** (hija de un Recorregut de
 * Vigilància)?
 *
 * No es un tipo de entidad distinto: es la MISMA `Action + Order`, y lo único
 * que la define es que su Order padre sea un recorrido. Lleva traje de datos
 * reducido —sin operarios ni maquinaria propios, que hereda del recorrido— y
 * sus horas NO cuentan: en un RV el trabajo se mide por los TRAMOS.
 *
 * ⚠️ El discriminador es el DATO, nunca la pantalla. Durante mucho tiempo el
 * traje reducido dependió de `location.pathname.startsWith('/surveillance-route/')`,
 * y por eso desaparecía al validar la actuación: la validación vive bajo
 * `/work-orders/`. No añadir gatings nuevos por path.
 *
 * `containerType` es el camino principal (denormalizado por el backend, viaja
 * gratis en los fragments `NEW_ORDER` y `NEW_ACTION`). `parentOrderType` es el
 * respaldo para documentos anteriores al backfill de `containerType`, para
 * quien ya tenga la Order padre cargada.
 *
 * Degradación segura: sin ninguno de los dos se comporta como una actuación
 * normal — muestra de más, nunca de menos.
 */
export const isBasicAction = (source: {
  containerType?: string | null;
  parentOrderType?: string | null;
}): boolean =>
  source.containerType === OrderType.SurveillanceRoute ||
  source.parentOrderType === OrderType.SurveillanceRoute;

/**
 * Campos que una **Actuació bàsica** NUNCA envía al guardar.
 *
 * Los operarios, los vehículos y la maquinaria son del Recorregut de
 * Vigilància: es él quien tiene el turno y las horas (una `LaborEntry` por
 * persona, ventana del primer al último tramo). La básica registra QUÉ se hizo
 * y DÓNDE, y por eso su wizard solo tiene Ubicació · Dades · Partides ·
 * Materials · Animals.
 *
 * Existe como helper único a propósito: el mismo despiste ya se repitió en los
 * DOS flujos que guardan una actuación (validación y cierre del wizard). Y no
 * es inocuo — `vehicles: []` el backend lo lee como «borra los VehicleOccupant
 * actuales», y `isExternal: null` se escribe tal cual en base de datos.
 *
 * El servidor lo blinda además por su cuenta (ignora estos campos en silencio
 * si el padre es un recorrido), pero el cliente tampoco debe pedirlo.
 *
 * Spec: docs/superpowers/specs/2026-08-05-actuacion-basica-sin-tripulacion-design.md
 */
const BASIC_ACTION_FORBIDDEN_FIELDS = [
  'userIds',
  'vehicles',
  'workVehicleIds',
  'auxMachineUnitIds',
  'isExternal',
  'isSimultaneous',
  'typeOfHour',
  'minutesTotals',
] as const;

export const omitCrewFieldsForBasicAction = <T extends Record<string, unknown>>(
  input: T,
  isBasic: boolean
): T => {
  if (!isBasic) return input;
  const clean = { ...input };
  BASIC_ACTION_FORBIDDEN_FIELDS.forEach((field) => {
    delete clean[field];
  });
  return clean;
};

// Function to generate container-type (Origen) options with translations.
// Reusa las labels EXISTENTES `common:orderTypes.*` (mismas que
// `getTypeOptions` de abajo) — no se crean keys i18n nuevas para los
// VALORES, solo para el header de columna (`common:tables.headers.origin`).
export const getContainerTypeOptions = (t: TFunction) =>
  CONTAINER_TYPE_VALUES.map((value) => ({
    label: t(`common:orderTypes.${value}`),
    value,
  }));

// Function to generate type options with translations
export const getTypeOptions = (t: TFunction) => [
  { label: t('common:orderTypes.generic'), value: 'generic' },
  { label: t('common:orderTypes.communication'), value: 'communication' },
  { label: t('common:orderTypes.subOrder'), value: 'subOrder' },
  { label: t('common:orderTypes.action'), value: 'action' },
  { label: t('common:orderTypes.accident'), value: 'accident' },
  {
    label: t('common:orderTypes.surveillanceRoute'),
    value: 'surveillanceRoute',
  },
  { label: t('common:orderTypes.campaign'), value: 'campaign' },
];
